import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { cn } from '@/utils/cn'
import { formatUserDisplayName } from '@/utils/formatters'
import { getInitials, getAvatarColor } from '@/utils/avatarUtils'
import type { UserSummary } from '@/api/shares'

interface ShareMentionsConfirmDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  users: UserSummary[]
  isLoading?: boolean
  onConfirm: () => void
}

/**
 * Confirms sharing the plan with users who were @mentioned but don't have
 * access yet — otherwise they'd never see the comment or get notified.
 */
export function ShareMentionsConfirmDialog({
  open,
  onOpenChange,
  users,
  isLoading,
  onConfirm,
}: ShareMentionsConfirmDialogProps) {
  const isPlural = users.length > 1

  return (
    <ConfirmationDialog
      open={open}
      onOpenChange={onOpenChange}
      title={
        isPlural
          ? 'Share plan with these people?'
          : 'Share plan with this person?'
      }
      description="They don't have access to this plan yet, so they won't see your comment or get notified unless you share it with them first."
      confirmText="Share & post"
      cancelText="Cancel"
      onConfirm={onConfirm}
      isLoading={isLoading}
    >
      <div className="space-y-2 py-2">
        {users.map(user => {
          const colors = getAvatarColor(user.id)
          return (
            <div key={user.id} className="flex items-center gap-2">
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarFallback
                  className={cn(colors.bg, colors.text, 'text-xs font-medium')}
                >
                  {getInitials(user.display_name, user.email)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="text-sm font-medium truncate">
                  {formatUserDisplayName(user)}
                </p>
                {user.email && (
                  <p className="text-xs text-muted-foreground truncate">
                    {user.email}
                  </p>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </ConfirmationDialog>
  )
}
